import { handleApiRequest, SparksAIIcon, useCheckTaskStatus } from "@cpj-ai/panel-npm-package";
import {
  AuthAccountUserTaskTaskNameChoices,
  CvGenerateResumeMutation,
  CvGenerateResumeMutationVariables,
  useCvGenerateResumeMutation,
} from "@graphql/generated";
import { Button } from "@heroui/react";
import { useProfile } from "@hooks/useProfile";
import React, { useCallback } from "react";
import toast from "react-hot-toast";

interface GenerateResumeFileButtonProps {
  hasResume: boolean;
  variant?: "compact" | "prominent";
  className?: string;
}

export const GenerateResumeFileButton: React.FC<GenerateResumeFileButtonProps> = ({
  hasResume,
  variant = "compact",
  className = "",
}) => {
  const [generateResume, { loading: generatingRequestLoading }] = useCvGenerateResumeMutation();
  const { fetchProfile, loading: getProfileLoading, profile } = useProfile();

  const { startCheckingPromise, isPolling } = useCheckTaskStatus({
    task: AuthAccountUserTaskTaskNameChoices.RENDER_CV_TEMPLATE,
  });

  const handleGenerateResume = useCallback(async () => {
    const generateResult = await handleApiRequest<CvGenerateResumeMutation, CvGenerateResumeMutationVariables>({
      apiFunction: generateResume,
      componentName: "ResumeGenerator",
      actionName: "generateResume",
      fallbackErrorMessage: "Failed to initiate resume generation. Please try again.",
      displaySuccessToast: false,
      extraErrorContext: {
        hasExistingResume: hasResume,
      },
    });

    if (!generateResult?.success || !generateResult?.result?.data?.cv.generateResume?.success) return;

    try {
      await toast.promise(startCheckingPromise(), {
        loading: hasResume ? "Your CV is being updated. Please wait" : "Your CV is being generated. Please wait",
        success: "Your CV is ready for download.",
        error: "Something went wrong. Please try again.",
      });
      fetchProfile();
    } catch (error) {
      toast.error("Something went wrong. Please try again.");

      console.error("Resume generation process failed:", error);
    }
  }, [generateResume, hasResume, startCheckingPromise, fetchProfile]);

  const generating = generatingRequestLoading || isPolling || getProfileLoading;

  if (variant === "prominent") {
    return (
      <Button
        radius="lg"
        className={`bg-gradient-to-r from-primary-500 to-primary-600 h-auto text-white px-8 py-2 md:py-3 text-sm md:text-base font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105 w-full md:w-auto ${className}`}
        size="lg"
        onClick={handleGenerateResume}
        isDisabled={generating || !profile.hasProfile}
        isLoading={generating}
        startContent={!generating && <SparksAIIcon className="w-5 h-5 stroke-white" viewBox="0 0 22 22" />}
      >
        {hasResume ? (
          <>{generating ? "Updating Resume..." : "Update Resume Now"}</>
        ) : (
          <>{generating ? "Generating Resume..." : "Generate Resume Now"}</>
        )}
      </Button>
    );
  }

  return (
    <div>
      <Button
        radius="md"
        className={`border-large ${className}`}
        size="sm"
        variant="bordered"
        color="primary"
        onClick={handleGenerateResume}
        isDisabled={generating || !profile.hasProfile}
        isLoading={generating}
      >
        {hasResume ? (
          <>{generating ? "...Updating Resume" : "Update Resume"}</>
        ) : (
          <>{generating ? "...Generating Resume" : "Generate Resume"}</>
        )}
      </Button>
    </div>
  );
};
